import type { Region } from '../types/documentModel';

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * A well-formed region: a positive whole page and a box with a positive size.
 * Coordinates are page fractions; values a little outside 0–1 are kept and
 * clipped when drawn.
 */
export const isRegion = (value: unknown): value is Region => {
  if (typeof value !== 'object' || value === null) return false;
  const { page, x, y, w, h } = value as Record<string, unknown>;

  return (
    isFiniteNumber(page) &&
    Number.isInteger(page) &&
    page > 0 &&
    isFiniteNumber(x) &&
    isFiniteNumber(y) &&
    isFiniteNumber(w) &&
    isFiniteNumber(h) &&
    w > 0 &&
    h > 0
  );
};

/** The well-formed regions of an untrusted list, as plain objects. */
export const parseRegions = (value: unknown): Region[] =>
  Array.isArray(value)
    ? value
        .filter(isRegion)
        .map(({ page, x, y, w, h }) => ({ page, x, y, w, h }))
    : [];

/** The string entries of an untrusted list (fact keys), de-duplicated. */
export const parseFactIds = (value: unknown): string[] =>
  Array.isArray(value)
    ? [
        ...new Set(
          value.filter(
            (entry): entry is string =>
              typeof entry === 'string' && entry.length > 0
          )
        ),
      ]
    : [];
